import { CONTENT_TYPES } from "@/utils/content";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useEffect, useRef } from "react";
import ArticleCard from "../items/ArticleCard";
import SpotlightCard from "../items/SpotlightCard";
import VideoCard from "../items/VideoCard";
import { fitIn } from "@/utils/imageHandler";
import { useIsMobile } from "@/hooks/useIsMobile";
import NewsCard from "../items/NewsCard";
import OpinionCard from "../items/OpinionCard";

const CarouselRelatedContent = ({ data, title }) => {
  const isMobile = useIsMobile();
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    const params = {
      slidesPerView: "auto",
      spaceBetween: 12,
      keyboard: true,
      freeMode: true,
      mousewheel: { forceToAxis: true, releaseOnEdges: false },
      breakpoints: {
        768: {
          navigation: {
            nextEl: nextRef.current,
            prevEl: prevRef.current,
          },
          spaceBetween: 40,
        },
      },
      injectStyles: [
        `
            :host .swiper {
               overflow-y: visible;
            }
         `,
      ],
    };
    Object.assign(swiperRef.current, params);
    swiperRef.current.initialize();
  }, []);
  return (
    <div className='container'>
      <p className='text-xl font-medium leading-none text-white lg:text-3xl font-display'>
        {title}
      </p>
      <div className='relative pt-4 lg:pt-8 group'>
        <swiper-container ref={swiperRef} init='false'>
          {data.map((item, index) => (
            <swiper-slide
              key={index}
              class='!w-auto rounded-md overflow-hidden'
            >
              {item.content_type.name == CONTENT_TYPES.SPECIAL_REPORTS && (
                <Link
                  href={`reports/${item.slug}`}
                  key={`report-${item.uuid}`}
                  className='relative block h-[180px] lg:h-[250px] aspect-[93/125]'
                >
                  <Image
                    src={fitIn(item.cover_image_url, 400)}
                    alt='Image'
                    objectFit='cover'
                    layout='fill'
                  />
                </Link>
              )}
              {item.content_type.name == CONTENT_TYPES.SPOTLIGHTS && (
                <SpotlightCard
                  cardHeight={"h-[180px] lg:h-[250px]"}
                  data={item}
                  key={`spotlight-${item.uuid}`}
                />
              )}
              {item.content_type.name == CONTENT_TYPES.OPINIONS && (
                <OpinionCard
                  cardHeight={"h-[180px] lg:h-[250px]"}
                  data={item}
                  key={`opinion-${item.uuid}`}
                />
              )}
              {item.content_type.name == CONTENT_TYPES.ARTICLES && (
                <ArticleCard
                  cardHeight={"h-[180px] lg:h-[250px]"}
                  data={item}
                  key={`article-${item.uuid}`}
                />
              )}
              {item.content_type.name == CONTENT_TYPES.NEWS && (
                <NewsCard
                  cardHeight={"h-[180px] lg:h-[250px]"}
                  data={item}
                  key={`news-${item.uuid}`}
                />
              )}
              {item.content_type.name == CONTENT_TYPES.VIDEOS && (
                <VideoCard
                  videoHeight='h-[150px]'
                  data={item}
                  key={`video-${item.uuid}`}
                />
              )}
              {item.content_type.name == "Podcast Episodes" && (
                <VideoCard
                  videoHeight='h-[150px]'
                  data={item}
                  podcast={true}
                  key={`video-${item.uuid}`}
                />
              )}
              {item.content_type.name == CONTENT_TYPES.INFOBYTES && (
                <Link
                  href={`infobytes/${item.slug}`}
                  key={`infobyte-${item.uuid}`}
                  className='relative block h-[180px] lg:h-[250px] aspect-[384/288]'
                >
                  <Image
                    src={fitIn(item.thumbnail_url, 400)}
                    layout='fill'
                    objectFit='cover'
                    alt='Image'
                  />
                </Link>
              )}
            </swiper-slide>
          ))}
        </swiper-container>
        {!isMobile && (
          <>
            <div className='absolute top-0 left-0 z-10 flex items-center w-16 h-full transition duration-300 -translate-x-full opacity-0 group-hover:translate-x-0 group-hover:opacity-100 bg-gradient-to-r from-background via-background/70 to-transparent'>
              <button type='button' ref={prevRef}>
                <CaretLeft size={32} color='#fff' />
              </button>
            </div>
            <div className='absolute top-0 right-0 z-10 flex items-center justify-end w-16 h-full pr-4 transition duration-300 translate-x-full opacity-0 group-hover:translate-x-0 group-hover:opacity-100 bg-gradient-to-l from-background via-background/70 to-transparent'>
              <button type='button' ref={nextRef}>
                <CaretRight size={32} color='#fff' />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CarouselRelatedContent;
